import { Button, Dialog, DialogTitle } from "@mui/material";
import { type FC, type ReactNode, useCallback, useContext, useState } from "react";
import { type FieldErrors, useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { SidebarContext } from "../../../../../utils/contexts/sidebar-context";
import { useAnalytics } from "../../../../analytics/analytics";
import { isEmailChannel } from "../../../../channels/types";
import type { MomentFormMoment } from "../../../types/moment-form-moment";
import type { SaveButtonProps } from "../types";
import { toastErrors } from "../utils";
import { SendTestToEmail } from "./send-test-to-email";
import { SendTestToRecipient } from "./send-test-to-recipient";

export const SendTestButton: FC<SaveButtonProps & { children?: ReactNode }> = ({
  formSaveState,
  handleSave,
  saveDataReady,
  children,
  ...rest
}) => {
  const [testDialog, setTestDialog] = useState<boolean>(false);

  const { handleSubmit, control, trigger, getValues, getFieldState } =
    useFormContext<MomentFormMoment>();
  const { setProgramsDirty } = useContext(SidebarContext);

  const {
    saving,
    setSaving,
    formDisabled: { "sent-test-button": disabled },
    publishing,
    setLastAction,
  } = formSaveState;

  const publish = useCallback(
    async (values: MomentFormMoment): Promise<void> => {
      setSaving(true);

      const saved = await handleSave({ ...values });
      if (saved?.status === "valid") {
        setProgramsDirty(true);
        setTestDialog(true);
      } else if (saved) {
        toast.error(saved.status === "invalid" ? saved.message : "Something went wrong");
      }
      setSaving(false);
    },
    [handleSave, setSaving, setProgramsDirty],
  );

  const publishErrorHandler = useCallback(
    async (errs: FieldErrors<MomentFormMoment>): Promise<void> => {
      await trigger();
      toastErrors(errs);
      const scheduleState = getFieldState("schedule");
      if (scheduleState.error) {
        toast.error("Schedule is not valid");
      }
      setSaving(false);
    },
    [setSaving, trigger, getFieldState],
  );
  useWatch({ control, name: ["channel", "id"] });
  const [channel, id] = getValues(["channel", "id"]);

  const { gaEvent } = useAnalytics();

  return (
    <>
      <Button
        data-analytics-id="moment-form-send-test"
        sx={{ ml: 2 }}
        loading={saving}
        variant="outlined"
        {...rest}
        onClick={() => {
          setLastAction("publish");
          void handleSubmit(publish, publishErrorHandler)().catch(console.error);
          gaEvent("send_test", { event_category: "moment" });
        }}
        disabled={disabled || !saveDataReady}
      >
        Send Test
      </Button>
      <Dialog
        open={testDialog}
        onClose={() => {
          setTestDialog(false);
          setSaving(false);
        }}
        aria-labelledby="alert-test-dialog-title"
        aria-describedby="alert-test-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-test-dialog-title">Send Test</DialogTitle>
        {isEmailChannel(channel) ? (
          <SendTestToEmail
            id={id}
            setSaving={setSaving}
            publishing={publishing}
            setTestDialog={setTestDialog}
          >
            {children}
          </SendTestToEmail>
        ) : (
          <SendTestToRecipient
            id={id}
            setSaving={setSaving}
            publishing={publishing}
            setTestDialog={setTestDialog}
            channel={channel}
          >
            {children}
          </SendTestToRecipient>
        )}
      </Dialog>
    </>
  );
};
